/**
 * `useUnreadNotificationCount` — W132-F5.
 *
 * Single source of truth for the unread-notification count. Hits Core's
 * `pollNotifications` with `unreadOnly=true&limit=1` so the response is
 * a cheap count + the latest row only (no full page). The bell badge
 * + the `/notifications` page both subscribe to this query so the two
 * surfaces can never drift out of sync — which was the W132-F5
 * symptom ("sidebar shows 1 but /notifications reports 0 unread").
 *
 * Cache invalidation: the query key starts with `NOTIFICATIONS_QUERY_KEY`
 * so the existing `qc.invalidateQueries({ queryKey:
 * NOTIFICATIONS_QUERY_KEY })` calls from `useMarkRead` /
 * `useMarkAllRead` / `useRealtimeNotifications` all bust this count
 * too — no new wiring needed on the mutation side.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import { NOTIFICATIONS_QUERY_KEY } from './use-notifications';

interface UnreadCountResponse {
  /** Total unread across the user's scope (Core's authoritative count). */
  readonly total: number;
  /** ISO timestamp of the newest unread, or null when none. */
  readonly latestAt: string | null;
}

/** Query key alias so the existing `NOTIFICATIONS_QUERY_KEY` invalidations cover us. */
export const UNREAD_COUNT_QUERY_KEY = [
  ...NOTIFICATIONS_QUERY_KEY,
  'unread-count',
] as const;

/**
 * Returns the unread-notification count. Polls every 30s while the
 * window is focused so the bell stays live without a websocket. Realtime
 * cache-busts (W6 socket bridge) already invalidate
 * `NOTIFICATIONS_QUERY_KEY` so this query repolls when a new notification
 * arrives.
 *
 * Returns 0 while loading / on error so the bell renders no-badge by
 * default (preferred over flashing a stale count from a different
 * cache slice).
 */
export function useUnreadNotificationCount(): UseQueryResult<UnreadCountResponse> {
  const { getAccessToken } = useAuth();
  const focused = useWindowFocus();
  return useQuery<UnreadCountResponse>({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: async () => {
      const raw = await apiFetch<{
        items?: Array<{ created_at?: string; createdAt?: string }>;
        total?: number;
      }>(
        '/api/v1/proxy/core/notifications?unreadOnly=true&limit=1',
        // App-wide 30s badge poll — degrade silently to no-badge on a
        // notifications-side outage; never fire the global serverError toast.
        { getAccessToken, skipServerErrorReport: true },
      );
      const total =
        typeof raw?.total === 'number'
          ? raw.total
          : Array.isArray(raw?.items)
            ? raw.items.length
            : 0;
      const latest = raw?.items?.[0];
      const latestAt = latest?.createdAt ?? latest?.created_at ?? null;
      return { total, latestAt };
    },
    refetchInterval: focused ? 30_000 : false,
    refetchOnWindowFocus: true,
    // #1714 — align the stale window with the 30s poll cadence. The bell
    // mounts on every route (Topbar + Sidebar), so a 10s stale window
    // refired this proxy on every navigation that took >10s, fanning the
    // 429 storm (72× across 23 pages in the 2026-06-25 nav benchmark).
    // 30s means back-to-back navigations inside a poll window read cache;
    // the timer poll + realtime/mutation invalidations keep the count live.
    staleTime: 30_000,
  });
}

/** Internal: window-focus tracker (mirrors use-notifications.ts's helper). */
function useWindowFocus(): boolean {
  const [focused, setFocused] = React.useState<boolean>(
    typeof document === 'undefined' ? true : !document.hidden,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onFocus = (): void => setFocused(true);
    const onBlur = (): void => setFocused(false);
    const onVisibility = (): void => setFocused(!document.hidden);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  return focused;
}
